import { useState, useEffect, useCallback, Fragment } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Inbox, RefreshCw, UserPlus, X, Mail, Clock, CheckCircle2, Copy,
  ChevronRight, ChevronDown, Sparkles, Calendar,
} from "lucide-react";
import ArchiveButton from "@/components/admin/ArchiveButton";

const STATUS_BADGES = {
  nouveau:  { label: "Nouveau",   cls: "bg-cyan-100 text-cyan-700",       Icon: Clock },
  converti: { label: "Converti",  cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  rejete:   { label: "Rejeté",    cls: "bg-gray-100 text-gray-500",       Icon: X },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function DetailField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-semibold text-emerald-700">{label}</div>
      <div className="text-sm text-slate-800">{value || <span className="text-slate-400 italic">—</span>}</div>
    </div>
  );
}

export default function LeadsPage() {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [credModal, setCredModal] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const r = await api.get(`/leads${params}`);
      setData(r.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleConvert = async (lead) => {
    if (!window.confirm(`Convertir ${lead.nom || lead.email} en client ?`)) return;
    setBusyId(lead.id);
    try {
      const r = await api.post(`/leads/${lead.id}/convert`);
      setCredModal({
        client: lead.nom || lead.email,
        username: r.data.portal_username,
        password: r.data.temporary_password,
        email_sent: r.data.email_sent,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (lead) => {
    if (!window.confirm("Rejeter ce lead ?")) return;
    setBusyId(lead.id);
    try {
      await api.post(`/leads/${lead.id}/dismiss`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const FILTERS = [
    { id: "", label: "Tous" },
    { id: "nouveau", label: "Nouveaux" },
    { id: "converti", label: "Convertis" },
    { id: "rejete", label: "Rejetés" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-2" data-testid="leads-page">

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Inbox className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
            <p className="text-sm text-gray-500">{data.count || data.items.length} lead(s)</p>
          </div>
        </div>
        <button
          onClick={load}
          className="p-2 rounded-lg hover:bg-gray-100 transition"
          aria-label="Rafraîchir"
          data-testid="leads-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.id || "all"}
            onClick={() => setStatusFilter(f.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
              statusFilter === f.id
                ? "bg-emerald-600 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
            data-testid={`leads-filter-${f.id || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="w-8 px-2 py-3"></th>
                <th className="text-left px-4 py-3 font-medium">Nom</th>
                <th className="text-left px-4 py-3 font-medium">Contact</th>
                <th className="text-left px-4 py-3 font-medium">Source</th>
                <th className="text-left px-4 py-3 font-medium">Statut</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Chargement...</td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400" data-testid="leads-empty">
                  Aucun lead
                </td></tr>
              )}
              {!loading && data.items.map((l) => {
                const status = STATUS_BADGES[l.status] || STATUS_BADGES.nouveau;
                const StatusIcon = status.Icon;
                const isBusy = busyId === l.id;
                const isExpanded = expandedId === l.id;
                const isRich = l.source === "demo_portal" || l.form_type === "demo_upgrade";
                return (
                  <Fragment key={l.id}>
                  <tr className="border-t border-gray-100 hover:bg-gray-50" data-testid={`lead-row-${l.id}`}>
                    <td className="px-2 py-3">
                      {isRich && (
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : l.id)}
                          className="p-1 rounded hover:bg-emerald-100 text-emerald-600"
                          title={isExpanded ? "Masquer les détails" : "Voir les détails"}
                          data-testid={`lead-expand-${l.id}`}
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{l.nom || "(anonyme)"}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <div>{l.email || "—"}</div>
                      {l.phone && <div className="text-xs text-gray-500">{l.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {isRich ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                          <Sparkles className="w-3 h-3" /> Démo portail
                        </span>
                      ) : (
                        <span className="text-gray-500">{l.source || "website"}</span>
                      )}
                      {l.rdv_demande && (
                        <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                          <Calendar className="w-3 h-3" /> RDV demandé
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${status.cls}`}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {l.status === "nouveau" && (
                          <>
                            <button
                              onClick={() => handleConvert(l)}
                              disabled={isBusy || !l.email}
                              title={!l.email ? "Email requis" : "Convertir en client"}
                              className="px-2.5 py-1.5 rounded-md text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition flex items-center gap-1"
                              data-testid={`lead-convert-${l.id}`}
                            >
                              <UserPlus className="w-3 h-3" /> Convertir
                            </button>
                            <button
                              onClick={() => handleDismiss(l)}
                              disabled={isBusy}
                              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition"
                              aria-label="Rejeter"
                              data-testid={`lead-dismiss-${l.id}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        <ArchiveButton
                          itemType="lead"
                          itemId={l.id}
                          itemLabel={`le prospect "${l.nom || l.email}"`}
                          onArchived={load}
                        />
                      </div>
                    </td>
                  </tr>
                  {isExpanded && isRich && (
                    <tr className="bg-emerald-50/40 border-t border-emerald-100" data-testid={`lead-details-${l.id}`}>
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                          <DetailField label="Adresse" value={l.adresse} />
                          <DetailField label="Méthode de contact" value={l.methode_contact} />
                          <DetailField label="Moment préféré" value={l.moment_contact} />
                          <DetailField label="RDV demandé"   value={l.rdv_demande ? `Oui — ${l.rdv_format === "presentiel" ? "En présence" : "Google Meet"}` : "Non"} />
                          <DetailField label="Secteur d'expertise" value={l.secteur_expertise} />
                        </div>
                        {l.message && (
                          <div className="mt-3 p-3 bg-white border border-emerald-100 rounded-lg">
                            <div className="text-[10px] uppercase font-semibold text-emerald-700 mb-1">Note du prospect</div>
                            <div className="text-sm text-slate-700 whitespace-pre-wrap">{l.message}</div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {credModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="leads-cred-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Client créé</h3>
                <p className="text-xs text-gray-500">{credModal.client}</p>
              </div>
            </div>
            <div className="space-y-3 mb-5">
              <CredRow label="Identifiant" value={credModal.username} />
              <CredRow label="Mot de passe temporaire" value={credModal.password} />
              {credModal.email_sent ? (
                <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> Email envoyé au client
                </p>
              ) : (
                <p className="text-xs text-amber-600">⚠ Email non envoyé — communiquez les credentials manuellement.</p>
              )}
            </div>
            <button
              onClick={() => setCredModal(null)}
              className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition"
              data-testid="leads-cred-close"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CredRow({ label, value }) {
  const copy = () => {
    navigator.clipboard?.writeText(value);
    toast.success("Copié");
  };
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-[11px] uppercase text-gray-500 mb-1">{label}</div>
      <div className="flex items-center justify-between">
        <code className="text-sm font-mono text-gray-900 break-all">{value}</code>
        <button onClick={copy} className="p-1.5 hover:bg-white rounded shrink-0 ml-2">
          <Copy className="w-3.5 h-3.5 text-gray-500" />
        </button>
      </div>
    </div>
  );
}
